// @flow
import { createSelector } from 'reselect';
import type { GlobalState } from '../rootReducer';
import { HYDRATE_CLIENT_STATE } from './client';
import UAParser from 'ua-parser-js';

// reducer
const DEFAULT_STATE = {
    deviceType: 'computer',
    microsoftBrowser: false,
    mobile: '',
    name: '',
};

export type State = {|
    ...typeof DEFAULT_STATE,
|};

export default function reducer(state: State = DEFAULT_STATE, action: any = {}) {
    switch (action.type) {
        case HYDRATE_CLIENT_STATE:
            return {
                ...state,
                ...action.payload.browser,
            };
        default:
            return state;
    }
}

/* SELECTORS */
const stateSelector = (state: GlobalState) => state.browser;

export const getUiBrowser = createSelector(stateSelector, state => state);

export const isUiMobile = createSelector(getUiBrowser, uiBrowser => uiBrowser.mobile);

export const isBrowserIE = createSelector(getUiBrowser, uiBrowser => {
    return Boolean(uiBrowser.name && uiBrowser.name.toLowerCase() === 'ie');
});

export const getDeviceType = createSelector(getUiBrowser, uiBrowser => uiBrowser.deviceType);

export const getMobileBrowserOS = createSelector(getUiBrowser, uiBrowser => {
    return uiBrowser.mobile ? uiBrowser.os : '';
});

export const isUiMicrosoftBrowser = createSelector(getUiBrowser, uiBrowser => uiBrowser.microsoftBrowser);

export const isUiTablet = createSelector(isUiMobile, mobile => {
    return mobile === 'tablet';
});

const getMinimumBrowserVersions = () => {
    return {
        chrome: 45,
        edge: 13,
        firefox: 41,
        ie: 11,
        safari: 9,
    };
};

export const getBrowserDataFromUA = (userAgentString: string) => {
    const parser = new UAParser();

    parser.setUA(userAgentString);
    const result = parser.getResult();

    let name = '';
    let os = '';
    let majorVersion = 0;
    let mobile = '';
    let deviceType = '';

    if (result && result.browser) {
        if (typeof result.browser.name === 'string') {
            // Googlebot don't have a name
            name = result.browser.name.toLowerCase();
        }
        if (result.browser.major) {
            majorVersion = parseInt(result.browser.major, 10);
        }
    }
    if (result && result.os) {
        if (typeof result.os.name === 'string') {
            os = result.os.name.toLowerCase();
        }
    }
    if (result && result.device) {
        deviceType = result.device.type || 'computer'; // [console, mobile, tablet, smarttv, wearable, embedded]

        if (result.device.type === 'mobile') {
            mobile = 'phone';
        } else if (result.device.type === 'tablet') {
            mobile = result.device.type;
        }
    }

    const minimumBrowserVersions = getMinimumBrowserVersions();

    let isOutdated = false;
    if (Object.prototype.hasOwnProperty.call(minimumBrowserVersions, name)) {
        // $FlowFixMe
        isOutdated = minimumBrowserVersions[name] > majorVersion;
    }

    const isGoogle = userAgentString.toLowerCase().includes('google');
    if (isGoogle) {
        isOutdated = false;
    }

    return {
        deviceType,
        isOutdated,
        majorVersion,
        microsoftBrowser: name === 'ie' || name === 'edge',
        mobile,
        name,
        os,
    };
};
